import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  SbkTransfersStorageService,
  TransferAdditions,
  TransferEntity,
  TransferFee,
} from '../custom-views/sbk-transfer/data-access/services/sbk-transfers-interceptor.storage.service';
import { buildPaymentsHandlerRegistry } from './payments/payments-interceptor.handlers';
import {
  PaymentCallType,
  PaymentDataLike,
  PaymentHandlerHelpers,
  PaymentRequestBody,
  PaymentsApiResponse,
  PaymentsHandlerRegistry,
  ResponseCaptureOptions,
} from './payments/payments-interceptor.models';

@Injectable()
export class TransfersInterceptor implements HttpInterceptor {
  private readonly handlers: PaymentsHandlerRegistry = buildPaymentsHandlerRegistry();

  private readonly handlerHelpers: PaymentHandlerHelpers = {
    buildAdditions: (paymentData, currencies, baseAdditions = {}) =>
      this.buildAdditions(paymentData, currencies, baseAdditions),
    setTransferTransactionInformation: (body, transferTransactionInformation) =>
      this.setTransferTransactionInformation(body, transferTransactionInformation),
    setTransferTransactionInformationAdditions: (transferTransactionInformation, additions) =>
      this.setTransferTransactionInformationAdditions(transferTransactionInformation, additions),
    setTransferRemittanceInformation: (transferTransactionInformation, paymentData) =>
      this.setTransferRemittanceInformation(transferTransactionInformation, paymentData),
    toCamelCaseKey: (value) => this.toCamelCaseKey(value),
  };

  constructor(private sbkTransfersStorageService: SbkTransfersStorageService) {}

  intercept<R, T>(req: HttpRequest<R>, next: HttpHandler): Observable<HttpEvent<T>> {
    if (this.isGetPaymentOrderRequest(req)) {
      return this.handleGetPaymentOrder(req, next);
    }

    const callType = this.resolveCallType(req);
    if (!callType) {
      return next.handle(req);
    }

    const paymentData = (this.sbkTransfersStorageService.paymentDataObj ?? null) as PaymentDataLike | null;
    if (!paymentData?.paymentType) {
      return this.handleDefaultPaymentOrder(req, next);
    }

    const body = req.body as PaymentRequestBody | null;
    if (!body) {
      return this.handleDefaultPaymentOrder(req, next);
    }

    const key = `${paymentData.paymentType}:${callType}`;
    const handler = this.handlers[key];

    if (!handler) {
      return this.handleDefaultPaymentOrder(req, next);
    }

    const result = handler(
      body,
      {
        paymentData,
        currencies: this.sbkTransfersStorageService.currenciesObj,
        callType,
      },
      this.handlerHelpers,
    );

    if (!result) {
      return this.handleDefaultPaymentOrder(req, next);
    }

    const handledRequest = req.clone({ body });
    return this.withTransferResponseCapture(next.handle(handledRequest), result.responseCapture);
  }

  private handleGetPaymentOrder<R, T>(req: HttpRequest<R>, next: HttpHandler): Observable<HttpEvent<T>> {
    const params = req.params.has('paymentTypes') ? req.params.delete('paymentTypes') : req.params;
    return this.withTransferResponseCapture(next.handle(req.clone({ params })), { captureAdditions: true });
  }

  private handleDefaultPaymentOrder<R, T>(req: HttpRequest<R>, next: HttpHandler): Observable<HttpEvent<T>> {
    if (this.isPostRequest(req) && this.isPaymentOrderRequest(req)) {
      return this.withTransferResponseCapture(next.handle(req), { captureAdditions: true });
    }
    return next.handle(req);
  }

  private resolveCallType(req: HttpRequest<unknown>): PaymentCallType | null {
    if (!this.isPostRequest(req)) {
      return null;
    }

    if (this.isValidateRequest(req)) {
      return 'validate';
    }

    if (this.isPaymentOrderRequest(req)) {
      return 'paymentOrder';
    }

    return null;
  }

  private isGetPaymentOrderRequest(req: HttpRequest<unknown>): boolean {
    return this.isGetRequest(req) && this.isPaymentOrderRequest(req);
  }

  private isGetRequest(req: HttpRequest<unknown>): boolean {
    return req.method.toUpperCase() === 'GET';
  }

  private isPostRequest(req: HttpRequest<unknown>): boolean {
    return req.method.toUpperCase() === 'POST';
  }

  private isValidateRequest(req: HttpRequest<unknown>): boolean {
    return req.url.endsWith('/validate');
  }

  private isPaymentOrderRequest(req: HttpRequest<unknown>): boolean {
    return req.url.endsWith('/payment-orders') || req.url.includes('/payment-orders/');
  }

  private isFxPayment(paymentData: PaymentDataLike, currencies: string[]): boolean {
    const initiator = paymentData?.initiator;
    const counterparty = paymentData?.counterparty;
    const amountGroup = paymentData?.remittanceInfo?.amountGroup;

    if (currencies.length > 1) return true;

    if (
      initiator &&
      counterparty &&
      amountGroup &&
      (initiator.currency !== counterparty.currency ||
        initiator.currency !== amountGroup.currency ||
        counterparty.currency !== amountGroup.currency)
    ) {
      return true;
    }

    return !!(initiator && amountGroup && initiator.currency !== amountGroup.currency);
  }

  private getFxData(paymentData: PaymentDataLike, currencies: string[]): { fromCurrency: string; toCurrency: string } {
    const initiator = paymentData?.initiator;
    const counterparty = paymentData?.counterparty;
    const amountGroup = paymentData?.remittanceInfo?.amountGroup;

    const fromCurrency = initiator?.currency;
    const toCurrency = counterparty?.currency || currencies.find((currency) => currency !== fromCurrency) || amountGroup?.currency;

    return {
      fromCurrency,
      toCurrency,
    };
  }

  private toCamelCaseKey(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (_match: string, chr: string) => chr.toUpperCase());
  }

  private setTransferTransactionInformation(
    body: PaymentRequestBody,
    transferTransactionInformation: Record<string, unknown>,
  ): void {
    body.transferTransactionInformation = transferTransactionInformation;
  }

  private setTransferTransactionInformationAdditions(
    transferTransactionInformation: Record<string, any>,
    additions: Record<string, unknown>,
  ): void {
    transferTransactionInformation.additions = additions;
  }

  private setTransferRemittanceInformation(
    transferTransactionInformation: Record<string, any>,
    paymentData: PaymentDataLike,
  ): void {
    transferTransactionInformation.remittanceInformation = paymentData?.remittanceInfo?.remittanceInformation;
  }

  private buildAdditions(
    paymentData: PaymentDataLike,
    currencies: string[],
    baseAdditions: Record<string, unknown> = {},
  ): Record<string, unknown> {
    if (this.isFxPayment(paymentData, currencies)) {
      return { ...this.getFxData(paymentData, currencies), ...baseAdditions };
    }
    return { ...baseAdditions };
  }

  private withTransferResponseCapture<T>(
    source$: Observable<HttpEvent<T>>,
    options: ResponseCaptureOptions,
  ): Observable<HttpEvent<T>> {
    return source$.pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.body) {
          const body = event.body as PaymentsApiResponse;
          if (options.captureFee) {
            this.captureTransferFee(body);
          }
          if (options.captureStatus) {
            this.captureTransferStatus(body);
          }
          if (options.captureAdditions) {
            this.captureAdditions(body);
          }
        }
      }),
    );
  }

  private captureTransferFee(body: PaymentsApiResponse): void {
    const transferFee = body.transferTransactionInformation?.transferFee;
    if (transferFee) {
      this.sbkTransfersStorageService.setTransferFee(transferFee as TransferFee);
    }
  }

  private captureTransferStatus(body: PaymentsApiResponse): void {
    if (body.status) {
      this.sbkTransfersStorageService.setTransferStatus(body.status as TransferEntity);
      this.sbkTransfersStorageService.setTransferResponse(body as TransferEntity);
    }
  }

  private captureAdditions(body: PaymentsApiResponse): void {
    if (body.additions) {
      this.sbkTransfersStorageService.setAdditions(body.additions as TransferAdditions);
    }
  }
}
